import { useEffect, useState } from "react";

import { useStorageManager } from "@services/useStorageManager";

/**
 * Reads a boolean flag from the storage manager and re-renders whenever it
 * changes through the storage manager (same-tab via its listener registry, or
 * cross-tab via the `storage` event). Returns `true` only when the stored value
 * is strictly `true`.
 *
 * @param key the storage manager key to read and subscribe to
 */
export const useReactiveStorageFlag = (key: string): boolean => {
  const storageManager = useStorageManager();
  const [isEnabled, setIsEnabled] = useState<boolean>(() => storageManager.get(key) === true);

  useEffect(() => {
    const listener = (value: unknown) => {
      setIsEnabled(value === true);
    };

    storageManager.listen(key, listener);
    // Re-sync in case the value changed between the initial render and this effect.
    setIsEnabled(storageManager.get(key) === true);

    return () => {
      storageManager.unlisten(key, listener);
    };
  }, [storageManager, key]);

  return isEnabled;
};
